/* eslint-disable no-underscore-dangle */
/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable no-restricted-syntax */
import lod_ from "lodash";
import {
	Badge,
	Card,
	CardContent,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	Icon,
	IconButton,
	Menu,
	MenuItem
} from "@mui/material";
import MDBox from "components/Basics/MDBox";
import MDTypography from "components/Basics/MDTypography";
import DefaultDataTable from "components/Custom/Tables/DefaultDataTable";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ThemeActions from "redux-react/actions/themeActions";
import ConfirmDialogButton from "components/Custom/Dialogs/ConfirmDialogButton";
import MDButton from "components/Basics/MDButton";
import { useLanguages } from "hooks/useLanguages";
import { socket } from "redux-react/middleware/ws";
import { display } from "redux-react/reducers/snackBarReducer";
import ConfirmDialog from "components/Custom/Dialogs/ConfirmDialog";
import { useTranslation } from "react-i18next";

import { useFormActions } from "hooks/useFormActions";
import LanguagesCell from "../ThemeCard/LanguagesCell";
import LanguagesHelp from "../ThemeCard/LanguagesHelp";

/**
 * Category Card component
 * Displays a card with category information and allows editing and deleting questions.
 * @param {Object} props - Component properties
 * @param {Array} props.folders - List of folders
 * @param {Object} props.answerItem - answerItem object to display
 * @param {Function} props.onEdit - Function to call when editing a theme
 * @param {Function} props.handleDeleteFolder - Function to call when deleting a theme
 * @returns {JSX.Element} Rendered ThemeCard component
 */
const CategoryCard = ({
	theme,
	folders,
	answerItem,
	onEdit,
	handleDeleteFolder,
	onAddFile = () => {},
	themeActions
}) => {
	const dispatch = useDispatch();
	const { profile } = useSelector(state => state);

	const { startCategoryTraining, startCategoryUntraining, translateElements } = themeActions;
	const { getItemsFromCollection, deleteItems } = useFormActions();

	const { languages } = useLanguages();
	const defaultLanguage = (languages ?? []).find(lang => lang.default) || {};
	const [selectedRows, setSelectedRows] = useState([]);
	const [categories, setCategories] = useState([]);

	const [menuRef, setMenuRef] = useState(null);
	const [menuValues, setMenuValues] = useState({});

	const [anchorElMenu, setAnchorElMenu] = useState(null);

	const { t, ready } = useTranslation(); // Hook to access translations
	const menuRefTrigger = useRef();

	const translateSelection = values => {
		const codes = values.map(row => row.code);
		translateElements(codes, answerItem.type);
	};

	const [confirmRetrainingDialog, setConfirmRetrainingDialog] = useState({
		open: false,
		handleConfirm: null
	});

	const [confirmChooseLanguageDialog, setConfirmChooseLanguageDialog] = useState({
		open: false,
		handleConfirm: null
	});

	const [chooseLanguageDialog, setChooseLanguageDialog] = useState({
		open: false,
		questions: [],
		handleSelect: null
	});

	/**
	 * Fetch categories for the theme
	 */
	const getCategories = async () => {
		const fetchedCategories = await getItemsFromCollection("answerItem", {
			query: {
				documentType: "answerType",
				type: "themeQuestion",
				parent: answerItem?.code
			},
			sort: {
				name: 1
			}
		});

		setCategories(fetchedCategories);
	};

	/**
	 * Start training process for selected questions
	 */
	const startTrainingProcess = values => {
		const alreadyTrained = values.some(
			row => !lod_.isNil(row.modelTrainingUUID) && !lod_.isEmpty(row.modelTrainingUUID)
		);

		const startTrainingProcess = (language, questionsToTranslateBefore = []) => {
			dispatch(
				display({
					message: t("ASSETS.THEME.questionsTrainingStarted"),
					type: "info"
				})
			);
			dispatch(
				ThemeActions.startQuestionsTraining(
					language,
					questionsToTranslateBefore.map(q => q.code),
					values.map(q => q.code),
					{
						name: theme?.name || "",
						description: theme?.description || ""
					},
					{
						name: answerItem?.name || "",
						description: answerItem?.description || ""
					},
					() => {
						dispatch(
							display({
								message: t("ASSETS.THEME.questionsTrainingFinish"),
								type: "success"
							})
						);
						// Refresh list
						getCategories();
					},
					err => {
						dispatch(
							display({
								message: err?.message,
								type: "error"
							})
						);
					}
				)
			);
		};

		const handleLanguageSelection = language => {
			setChooseLanguageDialog({
				open: false,
				handleSelect: null
			});
			// Check if all questions have this language
			const questionsWithoutLanguage = values.filter(row => {
				const content = (row?.content ?? [])[0] || {};
				const value = content[language.toLowerCase()] || content[language.toUpperCase()];
				if (lod_.isNil(value)) {
					return true;
				}
				const question = value?.question;
				const answer = value?.answer;
				if (
					lod_.isEmpty(question) ||
					lod_.isEmpty(answer) ||
					lod_.isNil(question) ||
					lod_.isNil(answer)
				) {
					return true;
				}
				return false;
			});

			if (!lod_.isEmpty(questionsWithoutLanguage)) {
				setConfirmChooseLanguageDialog({
					open: true,
					title: t("ASSETS.THEME.translateQuestionTitle"),
					content: t("ASSETS.THEME.translateQuestionContent", {
						language
					}),
					handleConfirm: () => {
						setConfirmChooseLanguageDialog({
							open: false,
							handleConfirm: null
						});
						startTrainingProcess(language, questionsWithoutLanguage);
					}
				});
			} else {
				startTrainingProcess(language);
			}
		};

		const startChooseLanguageDialog = () => {
			// Choose language Dialog
			setChooseLanguageDialog({
				open: true,
				questions: values,
				handleSelect: handleLanguageSelection
			});
		};

		if (alreadyTrained) {
			// ConfirmButton
			setConfirmRetrainingDialog({
				title: t("ASSETS.THEME.questionAlreadyTrainTitle"),
				content: t("ASSETS.THEME.questionAlreadyTrainContent"),
				open: true,
				handleConfirm: () => {
					setConfirmRetrainingDialog({
						open: false,
						handleConfirm: null
					});
					startChooseLanguageDialog();
				}
			});
		} else {
			// Directly launch
			startChooseLanguageDialog();
		}
	};

	/**
	 * Untrain questions
	 */
	const untrainSelection = values => {
		dispatch(
			ThemeActions.startQuestionsUntraining(
				values.map(q => q.code),
				() => {
					dispatch(
						display({
							message: t("ASSETS.THEME.questionsUntrainingFinish"),
							type: "success"
						})
					);
					// Refresh list
					getCategories();
				},
				err => {
					dispatch(
						display({
							message: err?.message,
							type: "error"
						})
					);
				}
			)
		);
	};

	const generateAnswers = values => {
		dispatch(
			display({
				message: `Génération des réponses en cours...`,
				type: "info"
			})
		);

		dispatch(
			ThemeActions.generateAnswers(
				values.map(q => q.code),
				res => {
					display({
						message: `Génération des réponses terminée`,
						type: "success"
					});
				},
				err => {
					display({
						message: t("ASSETS.THEME.cannotGenerateAnswer"),
						type: "error"
					});
				}
			)
		);
	};

	const getContent = content => {
		let defaultContent =
			content[defaultLanguage.code?.toLowerCase()] || content[defaultLanguage.code?.toUpperCase()];
		if (!lod_.isNil(defaultContent)) {
			return defaultContent;
		}

		const firstKey = Object.keys(content)[0];
		return content[firstKey];
	};

	const HEADER_ACTIONS = [
		<MDBox>
			<Badge
				badgeContent={selectedRows.length}
				color="error"
				anchorOrigin={{
					vertical: "top",
					horizontal: "right"
				}}
			>
				<MDButton
					sx={{ ml: 0.5 }}
					variant="contained"
					color="dark"
					onClick={e => {
						setAnchorElMenu(e.currentTarget);
					}}
				>
					<Icon>list_alt</Icon>&nbsp;Actions
				</MDButton>
			</Badge>
			{/* Menu */}
			<Menu
				anchorEl={anchorElMenu}
				open={Boolean(anchorElMenu)}
				onClose={() => setAnchorElMenu(null)}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "center"
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "center"
				}}
			>
				{/* Actions list */}
				<MenuItem disabled style={{ opacity: 1 }}>
					<MDTypography variant="caption">{t("ASSETS.THEME.actionList")}</MDTypography>
				</MenuItem>

				{/* New category */}
				<MenuItem
					onClick={() => {
						onAddFile(answerItem);
						setAnchorElMenu(null);
					}}
				>
					<Icon fontSize="medium">add</Icon>
					{t("ASSETS.THEME.ACTIONS.newQR")}
				</MenuItem>

				<Divider />

				{/* Actions selection */}
				<MenuItem disabled style={{ opacity: 1 }}>
					<MDTypography variant="caption">t('ASSETS.THEME.actionSelection')</MDTypography>
				</MenuItem>
				{/* Translate */}
				<MenuItem
					disabled={selectedRows.length < 1}
					onClick={() => {
						setAnchorElMenu(null);
						translateSelection(selectedRows);
						setSelectedRows([]);
					}}
				>
					<Icon fontSize="medium" sx={{ mr: 1 }}>
						translate
					</Icon>
					{t("ASSETS.THEME.translateQuestions")}
				</MenuItem>
				{/* Generate Q/R AI */}
				<MenuItem
					disabled={selectedRows.length < 1}
					onClick={() => {
						generateAnswers(selectedRows);
						setAnchorElMenu(null);
						setSelectedRows([]);
					}}
				>
					<Icon fontSize="medium" sx={{ mr: 1 }}>
						auto_awesome
					</Icon>
					{t("ASSETS.THEME.ACTIONS.generateAnswer")}
				</MenuItem>
				{/* Train */}
				<MenuItem
					disabled={selectedRows.length < 1}
					onClick={() => {
						startTrainingProcess(selectedRows);
						setAnchorElMenu(null);
						setSelectedRows([]);
					}}
				>
					<Icon fontSize="medium" sx={{ mr: 1 }}>
						model_training
					</Icon>
					{t("SETTINGS.train")}
				</MenuItem>
				{/* Untrain */}
				<MenuItem
					style={{ color: "red" }}
					disabled={selectedRows.length < 1}
					onClick={() => {
						untrainSelection(selectedRows);
						setAnchorElMenu(null);
						setSelectedRows([]);
					}}
				>
					<Icon fontSize="medium" sx={{ mr: 1 }}>
						model_training
					</Icon>
					{t("ASSETS.THEME.ACTIONS.untrain")}
				</MenuItem>

				{/* Delete */}
				<ConfirmDialogButton
					disabled={selectedRows.length < 1}
					onConfirm={async (e, values) => {
						const ids = selectedRows.map(row => row._id);
						await deleteItems(
							ids,
							"answerItem",
							{
								delete: true,
								actions: [
									"deleteThemeQuestionsKnowledge", // Delete all knowledge linked to this question
									"deleteChildAnswerType", // Answer type cave have childs with parent = answerItem.code, delete theme.
									"emptyCacheTheme" // Empty cache for theme
								]
							},
							() => {
								getCategories();
							}
						);

						setSelectedRows([]);
						setAnchorElMenu(null);
					}}
					component={
						<MenuItem
							disabled={selectedRows.length < 1}
							style={{
								color: "red"
							}}
						>
							<Icon fontSize="medium">delete</Icon>
							{t("SETTINGS.delete")}
						</MenuItem>
					}
					title={t("ASSETS.DELETE_CONFIRMATION.title")}
					content={t("ASSETS.THEME.confirmDeleteQuestions")}
				/>
			</Menu>
		</MDBox>
	];

	/**
	 * Get all languages from questions
	 * @param {Array} questions List of questions
	 * @returns {Array} List of languages codes
	 */
	const getAllLanguages = (questions = []) => {
		let allLanguages = [];
		for (let question of questions) {
			const content = (question?.content ?? [])[0] || {};
			const keys = Object.keys(content);
			for (let key of keys) {
				if (!allLanguages.includes(key)) {
					allLanguages.push(key);
				}
			}
		}
		return allLanguages;
	};

	useEffect(() => {
		/* WS */
		socket.on("knowledgeBaseThemeCategoryRefresh", getCategories);
		return () => {
			socket.off("knowledgeBaseThemeCategoryRefresh", getCategories);
		};
	}, [profile.assistantID]);

	useEffect(() => {
		setSelectedRows([]);
		getCategories();
	}, [folders, answerItem, profile.assistantID]);

	// Force menu position update when translations are ready
	useEffect(() => {
		if (ready && menuRefTrigger.current && Boolean(menuRef)) {
			// Small delay to ensure DOM is updated
			const timer = setTimeout(() => {
				if (menuRefTrigger.current) {
					menuRefTrigger.current.updatePosition();
				}
			}, 0);
			return () => clearTimeout(timer);
		}
		return undefined;
	}, [ready, menuRef, t]);

	if (lod_.isNil(answerItem)) {
		return null;
	}

	return (
		<MDBox
			sx={{
				height: "100%"
			}}
		>
			{/* Confirm choose language */}
			<ConfirmDialog
				open={confirmChooseLanguageDialog.open}
				title={confirmChooseLanguageDialog.title}
				content={confirmChooseLanguageDialog.content}
				handleClose={() => {
					setConfirmChooseLanguageDialog({
						open: false
					});
				}}
				handleConfirm={confirmChooseLanguageDialog.handleConfirm}
			/>
			{/* Choose language dialog */}
			<Dialog
				open={chooseLanguageDialog.open}
				onClose={() => {
					setChooseLanguageDialog({
						open: false,
						questions: []
					});
				}}
				fullWidth
				maxWidth="sm"
			>
				<DialogTitle>
					<MDTypography variant="h5">{t("ASSETS.THEME.ACTIONS.selectLanguage")}</MDTypography>
					<MDTypography variant="body2">
						{t("ASSETS.THEME.ACTIONS.selectLanguageDescription")}
					</MDTypography>
				</DialogTitle>
				<DialogContent>
					{getAllLanguages(chooseLanguageDialog.questions).map((language, index) => {
						const languageItem = (languages ?? []).find(lan => lan.code === language);
						if (lod_.isNil(languageItem)) {
							return null;
						}
						return (
							<MDBox
								display="flex"
								alignItems="center"
								sx={{
									mb: 1,
									borderRadius: "8px",
									padding: "5px 10px",
									marginRight: "10px",
									backgroundColor: "light.main",
									transition: "background-color 0.1s",
									"&:hover": { cursor: "pointer", backgroundColor: "grey.300" }
								}}
								onClick={() => {
									if (chooseLanguageDialog.handleSelect) {
										setChooseLanguageDialog({
											open: false,
											questions: []
										});
										chooseLanguageDialog.handleSelect(languageItem.code);
									}
								}}
							>
								{/* Logo */}
								{languageItem.logoURL && (
									<img
										src={languageItem.logoURL}
										alt={languageItem.code}
										style={{
											width: "20px",
											height: "20px",
											borderRadius: "50%",
											marginRight: "10px"
										}}
									/>
								)}
								{/* Name */}
								<MDTypography variant="h6">{languageItem.name}</MDTypography>
							</MDBox>
						);
					})}
				</DialogContent>
			</Dialog>
			{/* Confirm retraining */}
			<ConfirmDialog
				open={confirmRetrainingDialog.open}
				title={confirmRetrainingDialog.title}
				content={confirmRetrainingDialog.content}
				handleClose={() => {
					setConfirmRetrainingDialog({
						open: false
					});
				}}
				handleConfirm={confirmRetrainingDialog.handleConfirm}
			/>

			<Card
				sx={{
					height: "100%",
					borderRadius: "0px"
				}}
			>
				<CardContent sx={{ m: 0, p: 0, display: "flex", overflow: "auto" }}>
					<MDBox display="flex" flexDirection="column" sx={{ flex: 1 }}>
						<MDBox display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 3 }}>
							<MDTypography variant="h5" sx={{ ml: 3 }}>
								{answerItem.name}
							</MDTypography>
						</MDBox>

						<MDBox
							sx={{
								overflow: "auto"
							}}
						>
							<DefaultDataTable
								// Checkbox
								checkbox
								selectedRows={selectedRows}
								handleSelection={(newValues, original) => {
									if (lod_.isNil(original)) {
										// Orignal null = we trigger the column checkbox
										if (!lod_.isEmpty(selectedRows)) {
											setSelectedRows([]);
										} else {
											setSelectedRows(categories);
										}
									} else {
										setSelectedRows(newValues);
									}
								}}
								isSelectedRow={row => {
									return selectedRows.some(selectedRow => selectedRow?.code === row?.code);
								}}
								list={null}
								pagination={{
									defaultEntrie: 50,
									entriesPerPage: [25, 50, 76, 100]
								}}
								noMargin
								noHeader
								headerAction={HEADER_ACTIONS}
								filters={{}}
								canSearch
								table={{
									rows: categories,
									columns: [
										{ accessor: "_id", Header: "id" },
										{ accessor: "code", Header: "code" },
										{ accessor: "content", Header: "content" },
										{
											accessor: "name",
											Header: t("SETTINGS.PAGES.name"),
											Cell: ({ value, row, isSelected }) => {
												const content = getContent(row.original?.content?.[0] ?? {});

												if (lod_.isNil(content)) {
													return value;
												}

												const color = isSelected ? "white" : "dark";

												row.values.name = content.question + " " + content.answer;

												return (
													<MDBox>
														<MDTypography variant="h6" color={color}>
															{content.question}
														</MDTypography>
														<MDTypography variant="body2" color={color}>
															{content.answer}
														</MDTypography>
													</MDBox>
												);
											}
										},
										{
											accessor: "knowledgeUUID",
											Header: "Entrainée dans FAIQ",
											Cell: ({ value }) => {
												if (lod_.isNil(value) || lod_.isEmpty(value)) {
													return (
														<Icon fontSize="medium" color="error">
															close
														</Icon>
													);
												}
												return (
													<Icon fontSize="medium" color="success">
														check
													</Icon>
												);
											}
										},
										{ accessor: "documentType", Header: "documentType" },
										{ accessor: "type", Header: "type" },
										{
											accessor: "languages",
											Header: "Langues",
											Cell: LanguagesCell,
											Help: LanguagesHelp
										}
									],
									count: categories.length
								}}
								display={["name", "knowledgeUUID", "languages"]}
								compact
								actions={[
									<IconButton
										handleclick={({ values }, e) => {
											setMenuValues(values);
											setMenuRef(e.currentTarget);
											e.stopPropagation();
											e.preventDefault();
										}}
									>
										<Icon fontSize="medium">more_vert</Icon>
									</IconButton>
								]}
							/>
						</MDBox>
						{/* Menu actions selection */}
						<Menu
							open={Boolean(menuRef)}
							anchorEl={menuRef}
							onClose={() => {
								setMenuRef(null);
							}}
							action={menuRefTrigger}
						>
							{/* Options */}
							<MenuItem disabled style={{ opacity: 1 }}>
								<MDTypography variant="caption">{t("SETTINGS.edition")}</MDTypography>
							</MenuItem>
							<MenuItem
								disabled={menuValues.type === "multiCollection"}
								onClick={() => {
									onEdit(menuValues);
									setMenuRef(null);
								}}
							>
								<Icon fontSize="medium">edit</Icon>&nbsp;{t("SETTINGS.edit")}
							</MenuItem>

							<ConfirmDialogButton
								onConfirm={(e, values) => {
									handleDeleteFolder(values);
									setMenuRef(null);
								}}
								component={
									<MenuItem style={{ color: "red" }}>
										<Icon fontSize="medium">delete</Icon>&nbsp;{t("SETTINGS.delete")}
									</MenuItem>
								}
								rowValues={menuValues}
								displayAttribute="name"
							/>
							<Divider />
							{/* Actions */}
							<MenuItem disabled style={{ opacity: 1 }}>
								<MDTypography variant="caption">Actions</MDTypography>
							</MenuItem>

							{/* Translate */}
							<MenuItem
								onClick={() => {
									translateSelection([menuValues]);
									setMenuRef(null);
								}}
							>
								<Icon fontSize="medium">translate</Icon>&nbsp;{t("ASSETS.THEME.translateQuestions")}
							</MenuItem>
							{/* Generate Q/R AI */}
							<MenuItem
								onClick={() => {
									generateAnswers([menuValues]);
									setMenuRef(null);
								}}
							>
								<Icon fontSize="medium">auto_awesome</Icon>&nbsp;Générer la réponse
							</MenuItem>
							{/* Train */}
							<MenuItem
								onClick={() => {
									startTrainingProcess([menuValues]);
									setMenuRef(null);
								}}
							>
								<Icon fontSize="medium">model_training</Icon>&nbsp;{t("SETTINGS.train")}
							</MenuItem>
							{/* Untrain */}
							<MenuItem
								style={{ color: "red" }}
								onClick={() => {
									untrainSelection([menuValues]);
									setMenuRef(null);
								}}
							>
								<Icon fontSize="medium">model_training</Icon>&nbsp;Désentrainer
							</MenuItem>
						</Menu>
					</MDBox>
				</CardContent>
			</Card>
		</MDBox>
	);
};

export default CategoryCard;
